// src/components/DisplayJobSummaryData.tsx

import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleTwoTone,
  InfoCircleTwoTone,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Card,
  Col,
  Collapse,
  Descriptions,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { safeStringify } from "@/utils/api";

const { Title, Text } = Typography;

interface GenerationFlags {
  requested: Record<string, boolean>;
  completed: Record<string, boolean>;
}

interface TokenUsage {
  [key: string]: {
    input_tokens: number;
    output_tokens: number;
  };
}

interface JobSummaryData {
  changed: boolean;
  job_id: string;
  document_id: string;
  status: string;
  update_time: string;
  total_input_tokens: number;
  total_output_tokens: number;
  processing_cost: number;
  optimization_cost: number;
  generation_flags: GenerationFlags;
  token_usage: TokenUsage;
  progress_percentage: number;
  date_last_processed: string;
  date_last_optimized: string;
  total_pages: number;
  optimization_quality_score: number;
  error_message: string | null;
  ocr_required: boolean;
  ocr_engine: string | null;
  ocr_confidence: number | null;
  status_description: string;
  original_file_name: string;
  user_id: number;
}

interface DisplayJobSummaryDataProps {
  jobId: string;
  visible: boolean;
  onClose: () => void;
}

const GenerationFlagsComponent = React.memo(function GenerationFlagsComponent({
  flags,
}: {
  flags: Record<string, boolean>;
}) {
  return (
    <Descriptions
      bordered
      size="small"
      column={1}
      labelStyle={{ fontSize: "12px", padding: "4px" }}
      contentStyle={{ fontSize: "12px", padding: "4px" }}
    >
      {Object.entries(flags).map(([key, value]) => (
        <Descriptions.Item key={key} label={key.replace(/_/g, " ")}>
          {value ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          ) : (
            <CloseCircleTwoTone twoToneColor="#ff4d4f" />
          )}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
});

const DisplayJobSummaryData: React.FC<DisplayJobSummaryDataProps> = ({
  jobId,
  visible,
  onClose,
}) => {
  const [data, setData] = useState<JobSummaryData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  useEffect(() => {
    if (!visible) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get<JobSummaryData>(`/api/document_job_summary/${jobId}`);
        setData(response.data);
      } catch (err: any) {
        console.error("Error fetching job summary data:", safeStringify(err));
        setError(
          err.response?.data?.error ||
            "An unexpected error occurred while fetching job summary data.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobId, visible]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      // Initial setting in case the component mounts after a resize
      handleResize();
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const isMobile = useMemo(() => windowWidth <= 768, [windowWidth]);
  const isTablet = useMemo(() => windowWidth > 768 && windowWidth <= 1024, [windowWidth]);

  const renderStatusTag = (status: string) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "completed":
        return (
          <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
            {status.toUpperCase()}
          </Tag>
        );
      case "failed":
        return (
          <Tag icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />} color="error">
            {status.toUpperCase()}
          </Tag>
        );
      case "processing":
      case "processing_additional":
        return (
          <Tag icon={<LoadingOutlined spin />} color="processing">
            {status.toUpperCase()}
          </Tag>
        );
      default:
        return (
          <Tag icon={<InfoCircleTwoTone />} color="default">
            {status.toUpperCase()}
          </Tag>
        );
    }
  };

  const tokenUsageData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.token_usage).map(([task, tokens]) => ({
      task: isMobile && task.length > 20 ? `${task.substring(0, 17)}...` : task,
      "Input Tokens": tokens.input_tokens,
      "Output Tokens": tokens.output_tokens,
    }));
  }, [data, isMobile]);

  // Adjust YAxis label width based on device
  const yAxisWidth = useMemo(() => {
    if (isMobile) return 80;
    if (isTablet) return 100;
    return 120;
  }, [isMobile, isTablet]);

  // Adjust left margin based on device
  const leftMargin = useMemo(() => {
    if (isMobile) return 80;
    if (isTablet) return 100;
    return 150;
  }, [isMobile, isTablet]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? "100%" : "80%"}
      title={`Job Summary - ${jobId}`}
      centered
      style={{ padding: "16px" }}
      styles={{
        body: {
          maxHeight: isMobile ? "calc(100vh - 108px)" : "none",
          overflowY: "auto",
          padding: isMobile ? "8px" : "16px",
        },
      }}
      destroyOnClose
      maskClosable
      closable
    >
      {loading ? (
        <div className="flex justify-center items-center h-48">
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          icon={<ExclamationCircleTwoTone twoToneColor="#ff4d4f" />}
        />
      ) : data ? (
        <div className="p-2 space-y-2">
          {/* Job Overview */}
          <Card style={{ padding: isMobile ? "8px" : "12px" }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" size="small">
                  <Title
                    level={5}
                    style={{
                      marginBottom: "8px",
                      fontSize: isMobile ? "16px" : "20px",
                    }}
                  >
                    Job Overview
                  </Title>
                  <Descriptions
                    size="small"
                    column={1}
                    labelStyle={{ fontSize: isMobile ? "10px" : "12px", padding: "4px" }}
                    contentStyle={{ fontSize: isMobile ? "10px" : "12px", padding: "4px" }}
                  >
                    <Descriptions.Item label="Job ID">
                      <Text copyable>{data.job_id}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      {renderStatusTag(data.status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Update Time">
                      {new Date(data.update_time).toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Original File Name">
                      {data.original_file_name}
                    </Descriptions.Item>
                  </Descriptions>
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" size="small" className="w-full">
                  <Title
                    level={5}
                    style={{
                      marginBottom: "8px",
                      fontSize: isMobile ? "16px" : "20px",
                    }}
                  >
                    Progress
                  </Title>
                  <Progress
                    percent={data.progress_percentage}
                    status={
                      data.status === "completed"
                        ? "success"
                        : data.status === "processing_failed" ||
                            data.status.endsWith("_failed") ||
                            data.status.includes("generation_failed")
                          ? "exception"
                          : "active"
                    }
                    style={{ marginBottom: "8px" }}
                  />
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Total Pages"
                        value={data.total_pages}
                        valueStyle={{ fontSize: isMobile ? "14px" : "16px" }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Quality Score"
                        value={data.optimization_quality_score}
                        suffix="/ 100"
                        valueStyle={{ fontSize: isMobile ? "14px" : "16px" }}
                      />
                    </Col>
                  </Row>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Token Usage */}
          <Card style={{ padding: isMobile ? "8px" : "12px" }}>
            <Title
              level={5}
              style={{
                marginBottom: "8px",
                fontSize: isMobile ? "16px" : "20px",
              }}
            >
              Token Usage
            </Title>
            <div
              style={{
                width: "100%",
                minHeight: isMobile ? "400px" : "250px",
                overflowX: "auto",
              }}
            >
              <ResponsiveContainer
                width={isMobile ? Math.max(tokenUsageData.length * 120, windowWidth - 40) : "100%"}
                height={isMobile ? 400 : 250}
              >
                <BarChart
                  data={tokenUsageData}
                  layout="vertical"
                  margin={{
                    top: 20,
                    right: 30,
                    left: leftMargin,
                    bottom: 20,
                  }}
                >
                  <XAxis
                    type="number"
                    hide={false}
                    fontSize={isMobile ? 10 : 12}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <YAxis
                    dataKey="task"
                    type="category"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={yAxisWidth}
                  />
                  <Tooltip wrapperStyle={{ fontSize: isMobile ? "10px" : "12px" }} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? "10px" : "12px" }} />
                  <Bar dataKey="Input Tokens" stackId="a" fill="#1890ff" />
                  <Bar dataKey="Output Tokens" stackId="a" fill="#52c41a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Generation Flags */}
          <Card style={{ padding: isMobile ? "8px" : "12px" }}>
            <Title
              level={5}
              style={{
                marginBottom: "8px",
                fontSize: isMobile ? "16px" : "20px",
              }}
            >
              Generation Flags
            </Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  title="Requested"
                  style={{
                    padding: isMobile ? "8px" : "12px",
                    fontSize: isMobile ? "12px" : "14px",
                  }}
                >
                  <GenerationFlagsComponent flags={data.generation_flags.requested} />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card
                  size="small"
                  title="Completed"
                  style={{
                    padding: isMobile ? "8px" : "12px",
                    fontSize: isMobile ? "12px" : "14px",
                  }}
                >
                  <GenerationFlagsComponent flags={data.generation_flags.completed} />
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Additional Information */}
          <Collapse
            defaultActiveKey={["1"]}
            expandIconPosition="end"
            className={isMobile ? "text-sm" : ""}
            items={[
              {
                key: "1",
                label: "Additional Information",
                style: { fontSize: isMobile ? "14px" : "16px" },
                children: (
                  <>
                    {data.error_message && (
                      <Alert
                        message="Error Message"
                        description={data.error_message}
                        type="error"
                        showIcon
                        icon={<ExclamationCircleTwoTone twoToneColor="#ff4d4f" />}
                        style={{ marginBottom: "12px" }}
                      />
                    )}
                    <Descriptions
                      bordered
                      size="small"
                      column={1}
                      labelStyle={{ fontSize: isMobile ? "10px" : "12px", padding: "4px" }}
                      contentStyle={{ fontSize: isMobile ? "10px" : "12px", padding: "4px" }}
                    >
                      <Descriptions.Item label="OCR Required">
                        {data.ocr_required ? "Yes" : "No"}
                      </Descriptions.Item>
                      {data.ocr_engine && (
                        <Descriptions.Item label="OCR Engine">{data.ocr_engine}</Descriptions.Item>
                      )}
                      {data.ocr_confidence !== null && (
                        <Descriptions.Item label="OCR Confidence">
                          {data.ocr_confidence}%
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Status Description">
                        {data.status_description}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                ),
              },
            ]}
          />
        </div>
      ) : (
        <Alert
          message="No Data"
          description="No summary data available for this job."
          type="info"
          showIcon
          icon={<InfoCircleTwoTone />}
        />
      )}
    </Modal>
  );
};

export default DisplayJobSummaryData;
